import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { logout } from '../store/slices/authSlice';
import appLogger from '../utils/appLogger';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = ({ children, hideHeaderFooter = false }) => {
    const dispatch = useAppDispatch();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        appLogger.log('Logout', { action: 'logout' });
        dispatch(logout());
        navigate('/');
    };

    // Use restaurant from Redux state or fallback to props
    const restaurantDetails = restaurant || {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Header — light background */}
            {!hideHeaderFooter && (
            <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm safe-top">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-[4.25rem]">
                        {/* Logo + Brand */}
                        <Link to={user ? (user.role === 'customer' ? '/customer' : user.role === 'restaurantadmin' ? '/restaurant/menu' : user.role === 'superadmin' ? '/superadmin' : user.role === 'companyemployee' ? '/employee/dashboard' : '/') : '/'} className="flex items-center gap-3 min-w-0">
                            <motion.div
                                className="relative flex-shrink-0 ring-2 ring-amber-400/40 rounded-full overflow-hidden bg-slate-50"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ duration: 0.2 }}
                            >
                                <img
                                    src={restaurantDetails?.logo || "/restro-brand-logo.png"}
                                    alt="RestroAI"
                                    className="h-10 w-10 md:h-11 md:w-11 object-contain p-0.5"
                                />
                                <span className="absolute bottom-0.5 right-0.5 w-2 h-2 bg-emerald-400 rounded-full ring-2 ring-white animate-pulse" aria-hidden />
                            </motion.div>
                            <div className="min-w-0">
                                <span className="block text-sm font-semibold text-slate-800 tracking-wide truncate">
                                    {restaurantDetails?.name ? restaurantDetails.name : 'RestroAI'}
                                </span>
                                <span className="block text-xs text-slate-500 truncate">
                                    {user?.role === 'restaurantadmin' && 'Restaurant'}
                                    {user?.role === 'superadmin' && 'Super Admin'}
                                    {user?.role === 'companyemployee' && 'Employee'}
                                    {user?.role === 'customer' && 'Order & Dine'}
                                    {!user && 'Home'}
                                </span>
                            </div>
                        </Link>

                        {/* Desktop Nav — center */}
                        <nav className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className="flex items-center gap-0.5 rounded-full bg-slate-100 px-1 py-1 border border-slate-200/80">
                                {(() => {
                                    let navItems = [];
                                    if (user?.role === 'restaurantadmin') {
                                        navItems = [
                                            { to: "/restaurant/menu", label: "Dashboard", icon: "📊" },
                                            { to: "/restaurant/menu-fullscreen", label: "Menu", icon: "🍽️" },
                                            { to: "/restaurant/analytics-fullscreen", label: "Analytics", icon: "📈" },
                                            { to: "/restaurant/payments-fullscreen", label: "Payments", icon: "💳" },
                                            { to: "/restaurant/tables-fullscreen", label: "Tables", icon: "🪑" }
                                        ];
                                    } else if (user?.role === 'superadmin') {
                                        navItems = [
                                            { to: "/superadmin", label: "Dashboard", icon: "👑" },
                                            { to: "/superadmin/restaurants", label: "Restaurants", icon: "🏪" },
                                            { to: "/superadmin/restro-lite", label: "PocketBillingAI", icon: "📱" },
                                            { to: "/superadmin/restro-lite-leads", label: "PocketBillingAI Leads", icon: "📝" },
                                            { to: "/superadmin/leads", label: "Leads", icon: "📋" },
                                            { to: "/superadmin/employees", label: "Employees", icon: "👥" },
                                            { to: "/superadmin/employee-performance", label: "Performance", icon: "🏆" },
                                            { to: "/superadmin/analytics", label: "Analytics", icon: "📊" }
                                        ];
                                    } else if (user?.role === 'companyemployee') {
                                        const p = user.permissions || {};
                                        navItems = [];
                                        if (p.tabs?.dashboard !== false) {
                                            navItems.push({ to: "/employee/dashboard", label: "Dashboard", icon: "📊" });
                                        }
                                        if (p.tabs?.restroLiteShops) {
                                            navItems.push({ to: "/employee/restro-lite-shops", label: "My shops", icon: "📱" });
                                        }
                                        if (p.tabs?.restroLiteLeads) {
                                            navItems.push({ to: "/employee/restro-lite-leads", label: "Trial leads", icon: "📝" });
                                        }
                                        if (navItems.length === 0) {
                                            navItems.push({ to: "/employee/dashboard", label: "Dashboard", icon: "📊" });
                                        }
                                    } else if (user?.role === 'customer') {
                                        navItems = [
                                            { to: "/customer", label: "Menu", icon: "🍽️" },
                                            { to: "/customer/orders", label: "My Orders", icon: "📋" },
                                            { to: "/customer/profile", label: "Profile", icon: "👤" }
                                        ];
                                    } else {
                                        navItems = [{ to: "/", label: "Home", icon: "🏠" }];
                                    }
                                    return navItems.map((item) => (
                                        <motion.div key={item.to} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                            <Link
                                                to={item.to}
                                                className="flex items-center gap-2 px-4 py-2 rounded-full text-slate-600 hover:text-amber-600 hover:bg-white transition-colors duration-200 text-sm font-medium"
                                            >
                                                <span className="text-base opacity-90">{item.icon}</span>
                                                <span>{item.label}</span>
                                            </Link>
                                        </motion.div>
                                    ));
                                })()}
                            </div>
                        </nav>

                        {/* Right: User + Logout (desktop) / Hamburger (mobile) */}
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="hidden md:flex items-center gap-2 text-slate-500 text-sm">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
                                <span>RestroAI</span>
                            </div>
                            <motion.button
                                onClick={handleLogout}
                                className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-slate-700 hover:text-amber-700 hover:border-amber-400/60 hover:bg-amber-50 transition-all duration-200 text-sm font-medium"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span aria-hidden>🚪</span>
                                <span>Logout</span>
                            </motion.button>

                            <motion.button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="md:hidden p-2.5 rounded-xl text-slate-600 hover:text-amber-600 hover:bg-slate-100 transition-colors duration-200"
                                whileTap={{ scale: 0.95 }}
                                aria-expanded={isMobileMenuOpen}
                                aria-label="Toggle menu"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    {isMobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </motion.button>
                        </div>
                    </div>

                    {/* Mobile menu dropdown — revamped panel */}
                    <AnimatePresence>
                        {isMobileMenuOpen && (
                            <motion.div
                                className="md:hidden overflow-hidden"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                            >
                                <div className="mt-1 mb-2 border-t border-stone-200 bg-white overflow-hidden">
                                    {/* Orange accent stripe */}
                                    <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />

                                    {/* Nav label — full width */}
                                    <p className="w-full px-4 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                        Navigate
                                    </p>
                                    <nav className="w-full flex flex-col pb-2">
                                        {(() => {
                                            let navItems = [];
                                            if (user?.role === 'restaurantadmin') {
                                                navItems = [
                                                    { to: "/restaurant/menu", label: "Dashboard", icon: "📊" },
                                                    { to: "/restaurant/menu-fullscreen", label: "Menu", icon: "🍽️" },
                                                    { to: "/restaurant/analytics-fullscreen", label: "Analytics", icon: "📈" },
                                                    { to: "/restaurant/payments-fullscreen", label: "Payments", icon: "💳" },
                                                    { to: "/restaurant/tables-fullscreen", label: "Tables", icon: "🪑" }
                                                ];
                                            } else if (user?.role === 'superadmin') {
                                                navItems = [
                                                    { to: "/superadmin", label: "Dashboard", icon: "👑" },
                                                    { to: "/superadmin/restaurants", label: "Restaurants", icon: "🏪" },
                                                    { to: "/superadmin/restro-lite", label: "PocketBillingAI", icon: "📱" },
                                                    { to: "/superadmin/restro-lite-leads", label: "PocketBillingAI Leads", icon: "📝" },
                                                    { to: "/superadmin/leads", label: "Leads", icon: "📋" },
                                                    { to: "/superadmin/employees", label: "Employees", icon: "👥" },
                                                    { to: "/superadmin/employee-performance", label: "Performance", icon: "🏆" },
                                                    { to: "/superadmin/analytics", label: "Analytics", icon: "📊" }
                                                ];
                                            } else if (user?.role === 'companyemployee') {
                                                const p = user.permissions || {};
                                                navItems = [];
                                                if (p.tabs?.dashboard !== false) {
                                                    navItems.push({ to: "/employee/dashboard", label: "Dashboard", icon: "📊" });
                                                }
                                                if (p.tabs?.restroLiteShops) {
                                                    navItems.push({ to: "/employee/restro-lite-shops", label: "My shops", icon: "📱" });
                                                }
                                                if (p.tabs?.restroLiteLeads) {
                                                    navItems.push({ to: "/employee/restro-lite-leads", label: "Trial leads", icon: "📝" });
                                                }
                                                if (navItems.length === 0) {
                                                    navItems.push({ to: "/employee/dashboard", label: "Dashboard", icon: "📊" });
                                                }
                                            } else if (user?.role === 'customer') {
                                                navItems = [
                                                    { to: "/customer", label: "Menu", icon: "🍽️" },
                                                    { to: "/customer/orders", label: "My Orders", icon: "📋" },
                                                    { to: "/customer/profile", label: "Profile", icon: "👤" }
                                                ];
                                            } else {
                                                navItems = [{ to: "/", label: "Home", icon: "🏠" }];
                                            }
                                            return navItems.map((item, i) => (
                                                <motion.div
                                                    key={item.to}
                                                    className="w-full"
                                                    initial={{ opacity: 0, x: -8 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: i * 0.03 }}
                                                >
                                                    <Link
                                                        to={item.to}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-stone-700 hover:text-orange-600 hover:bg-orange-50 transition-colors group"
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-base group-hover:bg-orange-100 transition-colors">
                                                            {item.icon}
                                                        </span>
                                                        <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                                                        <svg className="w-4 h-4 text-stone-300 group-hover:text-orange-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    </Link>
                                                </motion.div>
                                            ));
                                        })()}
                                    </nav>

                                    {/* Logout footer — full width */}
                                    <div className="w-full px-4 pb-3 pt-1">
                                        <motion.button
                                            onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                                            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors text-sm font-semibold"
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-stone-200/80 text-sm">🚪</span>
                                            <span>Logout</span>
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </header>
            )}

            {/* Main Content */}
            <main className="flex-1">
                {children}
            </main>

            {/* Footer — light theme to match Home/Restaurants */}
            {!hideHeaderFooter && (
            <footer className="hidden md:block bg-white border-t border-stone-200 text-stone-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {/* Company Info */}
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center space-x-3 mb-6">
                                <img src="/restro-brand-logo.png" alt="RestroAI" className="h-10 w-10 object-contain rounded-full ring-2 ring-amber-400/30" />
                                <div>
                                    <h3 className="text-xl font-bold text-stone-900">RestroAI</h3>
                                    <p className="text-amber-600 text-sm font-medium">AI-Powered Restaurant Management</p>
                                </div>
                            </div>
                            <p className="text-stone-600 mb-6 max-w-md">
                                Transform your restaurant operations with cutting-edge AI technology.
                                Streamline management, boost revenue, and delight customers with intelligent insights.
                            </p>
                            <div className="flex space-x-4">
                                <motion.a
                                    href="#"
                                    className="w-10 h-10 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full flex items-center justify-center transition-all duration-300 text-stone-600 hover:text-amber-600"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <span>📧</span>
                                </motion.a>
                                <motion.a
                                    href="#"
                                    className="w-10 h-10 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full flex items-center justify-center transition-all duration-300 text-stone-600 hover:text-amber-600"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <span>📱</span>
                                </motion.a>
                                <motion.a
                                    href="#"
                                    className="w-10 h-10 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full flex items-center justify-center transition-all duration-300 text-stone-600 hover:text-amber-600"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <span>💬</span>
                                </motion.a>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="text-lg font-semibold mb-4 text-stone-900">Quick Links</h4>
                            <ul className="space-y-3">
                                {[
                                    { label: "Dashboard", icon: "📊" },
                                    { label: "Analytics", icon: "📈" },
                                    { label: "Menu Management", icon: "🍽️" },

                                ].map((item, index) => (
                                    <motion.li
                                        key={item.label}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                    >
                                        <a href="#" className="flex items-center space-x-2 text-stone-600 hover:text-amber-600 transition-colors duration-300 group">
                                            <span className="group-hover:scale-110 transition-transform duration-300">
                                                {item.icon}
                                            </span>
                                            <span>{item.label}</span>
                                        </a>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>

                        {/* Support */}
                        <div>
                            <h4 className="text-lg font-semibold mb-4 text-stone-900">Support</h4>
                            <ul className="space-y-3">
                                {[
                                    { label: "Help Center", icon: "❓", path: null },
                                    { label: "Privacy Policy", icon: "🔒", path: "/privacy-policy" },
                                    { label: "Terms of Service", icon: "📋", path: "/terms-of-service" },
                                    { label: "Contact Us", icon: "📞", path: "/contact-us" }
                                ].map((item, index) => (
                                    <motion.li
                                        key={item.label}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                    >
                                        {item.path ? (
                                            <Link to={item.path} className="flex items-center space-x-2 text-stone-600 hover:text-amber-600 transition-colors duration-300 group">
                                                <span className="group-hover:scale-110 transition-transform duration-300">
                                                    {item.icon}
                                                </span>
                                                <span>{item.label}</span>
                                            </Link>
                                        ) : (
                                            <a href="#" className="flex items-center space-x-2 text-stone-600 hover:text-amber-600 transition-colors duration-300 group">
                                                <span className="group-hover:scale-110 transition-transform duration-300">
                                                    {item.icon}
                                                </span>
                                                <span>{item.label}</span>
                                            </a>
                                        )}
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Section */}
                    <div className="mt-12 pt-8 border-t border-stone-200">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-stone-500 text-sm">System Status: Online</span>
                            </div>
                            <div className="flex items-center space-x-6 text-stone-500 text-sm">
                                <span>Version 2.1.0</span>
                                <span>•</span>
                                <span>Last updated: {new Date().toLocaleDateString()}</span>
                            </div>
                        </div>
                        <div className="mt-4 text-center">
                            <p className="text-stone-500 text-sm">
                                © {new Date().getFullYear()} RestroAI. All rights reserved. | Made with ❤️ for restaurants worldwide
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
            )}
        </div>
    );
};

export default Layout;
